import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import {
  CONVERSION_READINESS_LABELS,
  CONVERSION_STAGE_LABELS,
  TRIAL_STATUS_LABELS,
  TRIAL_SUBQUEUES,
  readinessBadgeClass,
  trackDealerTrialEvent,
} from "../../../constants/automapDealerTrialConstants";
import { fetchTrialsDashboard } from "../../../services/automapDealerTrialApi";
import { AdminDealerNav } from "./dealerAdminShared";

function pathFilters(pathname) {
  if (pathname.endsWith("/active")) return { queue: "active" };
  if (pathname.endsWith("/ending-soon")) return { endingSoon: "true" };
  if (pathname.endsWith("/ready-to-convert")) return { readyToConvert: "true" };
  if (pathname.endsWith("/needs-attention")) return { needsAttention: "true" };
  return {};
}

function SummaryCard({ label, value }) {
  return (
    <div className="dashboard-summary-card">
      <span className="dashboard-summary-label">{label}</span>
      <strong className="dashboard-summary-value">{value ?? "—"}</strong>
    </div>
  );
}

function AdminDealerTrialsPageContent() {
  const location = useLocation();
  const preset = useMemo(() => pathFilters(location.pathname), [location.pathname]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("daysRemaining");
  const [foundingOnly, setFoundingOnly] = useState(false);
  const [noFollowUp, setNoFollowUp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const filters = { ...preset, sort };
      if (foundingOnly) filters.foundingOnly = "true";
      if (noFollowUp) filters.noFollowUp = "true";
      const data = await fetchTrialsDashboard(filters);
      setPayload(data);
      trackDealerTrialEvent("dealer_trials_dashboard_viewed", filters);
    } catch (err) {
      setError(err.message || "No se pudo cargar trials.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [preset, sort, foundingOnly, noFollowUp]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = payload?.rows || [];
  const summary = payload?.summary || {};

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionarios"
        title="Seguimiento de trials"
        description="Periodos de prueba, readiness de conversión y follow-ups."
        navigation={<AdminDealerNav active="trials" />}
      />
      <main className="admin-console__main wrap">
        <nav className="admin-console__nav-tabs" style={{ marginBottom: "1rem" }}>
          {TRIAL_SUBQUEUES.map((item) => (
            <Link key={item.key} to={item.href} className={location.pathname === item.href ? "is-active" : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>

        {error ? <AdminStatePanel tone="danger" title="Error" message={error} onRetry={load} retryLabel="Reintentar" /> : null}

        {!loading && summary ? (
          <div className="dashboard-summary-grid" style={{ marginBottom: "1rem" }}>
            <SummaryCard label="Trials activos" value={summary.activeTrials} />
            <SummaryCard label="Por vencer (7d)" value={summary.endingSoon} />
            <SummaryCard label="Vencidos" value={summary.expiredTrials} />
            <SummaryCard label="Listos convertir" value={summary.readyToConvert} />
            <SummaryCard label="Requieren atención" value={summary.needsAttention} />
            <SummaryCard label="Convertidos mes" value={summary.convertedThisMonth} />
            <SummaryCard label="Perdidos mes" value={summary.lostThisMonth} />
            <SummaryCard label="Readiness prom." value={summary.averageReadinessScore} />
            <SummaryCard label="Leads trial" value={summary.totalTrialLeads} />
            <SummaryCard label="WhatsApp trial" value={summary.totalTrialWhatsappClicks} />
          </div>
        ) : null}

        <div className="dealer-state-box" style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
          <label>
            Ordenar
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="daysRemaining">Días restantes</option>
              <option value="readinessScore">Readiness</option>
              <option value="leadsDuringTrial">Leads</option>
              <option value="responseRate">Respuesta</option>
              <option value="nextFollowUp">Próximo follow-up</option>
              <option value="trialEndAt">Fin trial</option>
            </select>
          </label>
          <label>
            <input type="checkbox" checked={foundingOnly} onChange={(e) => setFoundingOnly(e.target.checked)} />
            Solo fundadores
          </label>
          <label>
            <input type="checkbox" checked={noFollowUp} onChange={(e) => setNoFollowUp(e.target.checked)} />
            Sin follow-up
          </label>
          <Link className="dealer-portal-btn" to="/admin/playbook/dealer-trials">
            Playbook trials
          </Link>
        </div>

        {loading ? <p className="dealer-state-box">Cargando trials…</p> : null}

        {!loading && !rows.length ? (
          <AdminStatePanel tone="muted" title="Sin trials" message="No hay dealers que coincidan con los filtros." />
        ) : null}

        {!loading && rows.length ? (
          <div className="dealer-state-box" style={{ overflowX: "auto" }}>
            <table className="dealer-inventory-table">
              <thead>
                <tr>
                  <th>Dealer</th>
                  <th>Ciudad</th>
                  <th>Estado</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Días</th>
                  <th>Owner</th>
                  <th>Listings</th>
                  <th>Leads</th>
                  <th>WA</th>
                  <th>Resp.</th>
                  <th>Frescura</th>
                  <th>Score</th>
                  <th>Readiness</th>
                  <th>Stage</th>
                  <th>Follow-up</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.dealerId}>
                    <td>
                      <Link to={`/admin/dealers/${row.dealerId}/trial`}>{row.businessName}</Link>
                      {row.isFoundingDealer ? " · F" : ""}
                    </td>
                    <td>{row.city || "—"}</td>
                    <td>{TRIAL_STATUS_LABELS[row.trialStatus] || row.trialStatus}</td>
                    <td>{row.trialStartAt?.slice(0, 10) || "—"}</td>
                    <td>{row.trialEndAt?.slice(0, 10) || "—"}</td>
                    <td>{row.daysRemaining ?? "—"}</td>
                    <td>{row.assignedOwner || "—"}</td>
                    <td>{row.activeListings}</td>
                    <td>{row.leadsDuringTrial}</td>
                    <td>{row.whatsappClicksDuringTrial}</td>
                    <td>{row.responseRate != null ? `${row.responseRate}%` : "—"}</td>
                    <td>{row.inventoryFreshness != null ? `${row.inventoryFreshness}%` : "—"}</td>
                    <td>{row.readinessScore ?? "—"}</td>
                    <td>
                      <span className={readinessBadgeClass(row.conversionReadiness)}>
                        {CONVERSION_READINESS_LABELS[row.conversionReadiness] || row.conversionReadiness}
                      </span>
                    </td>
                    <td>{CONVERSION_STAGE_LABELS[row.conversionStage] || row.conversionStage}</td>
                    <td>{row.nextFollowUpAt?.slice(0, 10) || "—"}</td>
                    <td>
                      <Link to={`/admin/dealers/${row.dealerId}/trial`}>Abrir</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminDealerTrialsPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerTrialsPageContent />
    </RequirePermission>
  );
}
